import * as api from "brandguard/api"

export const assetTypes = ["image", "text", "document", "video"] as const
export type AssetType = (typeof assetTypes)[number]

export const anyUpload = "any" as const
export const uploadChoices = [...assetTypes, anyUpload] as const
export type UploadChoice = (typeof uploadChoices)[number]

export const acceptedMimetypes: Record<AssetType, Record<string, string[]>> = {
	image: { "image/png": [".png"], "image/jpeg": [".jpeg", ".jpg"], "image/webp": [".webp"] },
	text: { "text/csv": [".csv"], "text/plain": [".txt"] },
	document: { "application/pdf": [".pdf"] },
	video: { "video/mp4": [".mp4"], "video/quicktime": [".mov"] },
}

export type UnifiedItem =
	| { event_type: "image"; data: api.ImageSearchResponseItem }
	| { event_type: "text"; data: api.TextSearchResponseItem }
	| { event_type: "document"; data: api.DocumentSearchResponseItem }
	| { event_type: "video"; data: api.VideoSearchResponseItem }

// unifiedItemPosition is the item's place in the created_at, id descending order
// every search endpoint returns. The fraction is padded so positions compare as
// strings at full precision.
export function unifiedItemPosition(item: UnifiedItem): string {
	const created = item.data.media?.created_at ?? ""
	const [, seconds = created, fraction = "", zone = ""] = /^(.*T\d\d:\d\d:\d\d)(?:\.(\d+))?(.*)$/.exec(created) ?? []
	return `${seconds}.${fraction.padEnd(9, "0")}${zone} ${item.data.media?.id ?? ""}`
}

export function unifiedItemMediaId(item: UnifiedItem): string | undefined {
	return item.data.media?.id
}

export function unifiedItemEventId(item: UnifiedItem): string | undefined {
	return item.data.event?.id
}

// unifiedItemDisplay is the label the upload progress list shows for an item;
// text media carries no description so its id stands in.
export function unifiedItemDisplay(item: UnifiedItem): string {
	return (item.event_type === "text" ? item.data.media?.id : item.data.media?.description) ?? ""
}

export function assetTypeLabel(t: AssetType): string {
	switch (t) {
		case "image":
			return "Image"
		case "text":
			return "Text"
		case "document":
			return "Document"
		case "video":
			return "Video"
	}
}

// assetTypeForFile resolves which asset type a file uploads as. The browser's
// mimetype is checked first; the extension is the fallback because some
// platforms report CSV as application/vnd.ms-excel.
export function assetTypeForFile(f: File): AssetType | undefined {
	const dot = f.name.lastIndexOf(".")
	const ext = dot < 0 ? "" : f.name.slice(dot).toLowerCase()
	return assetTypes.find(
		(t) => f.type in acceptedMimetypes[t] || Object.values(acceptedMimetypes[t]).some((exts) => exts.includes(ext)),
	)
}
